<template>
  <div class="setup-hub-expansion-panel">
    <div class="setup-hub-expansion-panel__content">
      <v-expansion-panels
        v-if="setupHubData?.steps"
        flat
        v-model="expandedPanels"
        class="expansion-panel-wrapper"
      >
        <v-expansion-panel v-for="stepKey in setupHubSteps" :key="stepKey" :value="stepKey">
          <v-expansion-panel-title
            min-height="56px"
            class="expansion-panel-header"
            :class="{ 'expansion-panel-header--empty': !getStepItems(stepKey).length }"
            @click="handleGtmOnStepClick(stepKey)"
          >
            <template v-slot:actions />
            <template v-slot:default="{ expanded }">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center">
                  <v-icon
                    :class="{ 'rotated-90-deg': !expanded }"
                    icon="$chevronUp"
                    color="primary"
                    class="mr-2 rotated"
                  />
                  <div class="subtitle2">
                    {{ $t(`setupHub.steps.${stepKey}`) }}
                  </div>
                </div>
                <v-icon v-if="showAllStepsInSectionCompleted(stepKey)" icon="$check" />
              </div>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text v-if="getStepItems(stepKey).length">
            <div class="step-items-list">
              <div
                v-for="(item, index) in getStepItems(stepKey)"
                :key="`${stepKey}-${index}`"
                class="step-item"
                @click="handleStepItemClick(item)"
              >
                <div class="d-flex align-center justify-space-between w-100">
                  <div class="d-flex align-center">
                    <v-icon
                      :icon="getStatusIcon(item.status)"
                      :color="getStatusIconColor(item.status)"
                      size="20"
                      class="mr-3"
                    />
                    <div class="subtitle2" :class="getStepItemTitleClass(item)">
                      {{ $t(`setupHub.subSteps.${item.step}.title`) }}
                    </div>
                  </div>

                  <div class="d-flex align-center" v-if="item.status !== SetupHubStatus.COMPLETED">
                    <div class="body2 text-indigo-medium text-no-wrap">
                      ~{{ $t(`setupHub.subSteps.${item.step}.time`) }}
                    </div>
                    <v-icon icon="$chevronRight" size="24" class="step-item-chevron" />
                  </div>
                </div>
              </div>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-card
        class="my-4 mx-5"
        v-if="areAllStepsCompleted"
        data-testid="setup-hub-all-steps-completed-card"
      >
        <div class="text-center mx-5 my-5">
          <img src="/images/setup-hub/all-steps-completed.svg" />

          <div
            class="mt-3 headline5 text-indigo-purple"
            v-html="$t('setupHub.allStepsCompletedCard.title')"
          />

          <div class="mt-3 body2">
            {{ $t("setupHub.allStepsCompletedCard.description1") }}
          </div>

          <div class="mt-3 body2 text--semibold">
            {{ $t("setupHub.allStepsCompletedCard.description2") }}
          </div>

          <v-btn class="mt-4 mb-4" color="primary" rounded size="large" @click="dismissSetupHub()">
            {{ $t("setupHub.dismissSetupHub") }}
          </v-btn>
        </div>
      </v-card>
    </div>
    <div class="setup-hub-expansion-panel--footer" v-if="!areAllStepsCompleted">
      <v-tooltip open-delay="300" location="top">
        <template #activator="{ props }">
          <div
            v-bind="props"
            class="body2 cursor-pointer text-indigo-medium"
            @click="dismissSetupHub()"
          >
            {{ $t("setupHub.dismissSetupHub") }}
          </div>
        </template>
        <span v-html="$t('setupHub.dismissSetupHubTooltip')"></span>
      </v-tooltip>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { SetupHubStepItem } from "@/_store/setup-hub.module.ts";
import { useSetupHubStore } from "@/_store/setup-hub.module.ts";
import { storeToRefs } from "pinia";
import { SetupHubStatus, SetupHubStepCategory } from "@/constants/setup-hub.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    modelValue: {
      type: String as PropType<SetupHubStepCategory>,
      required: true,
    },
  },
  emits: ["update:modelValue", "step-item-click"],
  setup(props, { emit }) {
    const setupHubStore = useSetupHubStore();
    const { data: setupHubData, completedSteps, totalSteps } = storeToRefs(setupHubStore);
    const { dismissOnboarding } = setupHubStore;

    const expandedPanels = computed({
      get: () => props.modelValue,
      set: (value: SetupHubStepCategory) => {
        emit("update:modelValue", value);
      },
    });

    const setupHubSteps = computed(() => {
      return Object.keys(setupHubData?.value?.steps || {}) as SetupHubStepCategory[];
    });

    const areAllStepsCompleted = computed(() => {
      return completedSteps.value === totalSteps.value;
    });

    const getStepItems = (stepKey: SetupHubStepCategory): SetupHubStepItem[] => {
      if (!setupHubData.value?.steps) {
        return [];
      }
      return setupHubData.value.steps[stepKey] || [];
    };

    const getStatusIcon = (status: SetupHubStatus) => {
      switch (status) {
        case SetupHubStatus.SKIPPED:
          return "icon-skip-xs";
        default:
          return "icon-success-xs";
      }
    };

    const showAllStepsInSectionCompleted = (stepKey: SetupHubStepCategory) => {
      const allSteps = getStepItems(stepKey);
      return allSteps.every(
        (step: SetupHubStepItem) =>
          step.status === SetupHubStatus.COMPLETED || step.status === SetupHubStatus.SKIPPED
      );
    };

    const getStatusIconColor = (status: SetupHubStatus) => {
      switch (status) {
        case SetupHubStatus.COMPLETED:
          return "green-base";
        default:
          return "primary";
      }
    };

    const getStepItemTitleClass = (item: SetupHubStepItem) => {
      switch (item.status) {
        case SetupHubStatus.COMPLETED:
        case SetupHubStatus.SKIPPED:
          return "text-indigo-medium text-decoration-line-through";
        default:
          return "text-primary";
      }
    };

    const dismissSetupHub = () => {
      trackGtmEvent(GtmAction.SETUP_HUB, GTMParams.DISMISSED);
      dismissOnboarding();
    };

    const handleGtmOnStepClick = (step: string) => {
      trackGtmEvent(GtmAction.SETUP_HUB, `${startCase(step)} - ${GTMParams.CLICKED}`);
    };

    const handleStepItemClick = (item: SetupHubStepItem) => {
      handleGtmOnStepClick(item.step);
      emit("step-item-click", item);
    };

    return {
      setupHubData,
      expandedPanels,
      setupHubSteps,
      getStepItems,
      getStatusIconColor,
      getStepItemTitleClass,
      handleStepItemClick,
      getStatusIcon,
      showAllStepsInSectionCompleted,
      dismissSetupHub,
      handleGtmOnStepClick,
      areAllStepsCompleted,
      SetupHubStatus,
    };
  },
});
</script>

<style scoped lang="scss">
.expansion-panel-wrapper {
  background-color: transparent;
}

.expansion-panel-header {
  height: 56px;
  background-color: rgb(var(--v-theme-indigo-faint));
}

.step-items-list {
  padding: 0;
}

.step-item {
  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    background-color: rgb(var(--v-theme-indigo-faint));
  }

  &--active {
    background-color: rgb(var(--v-theme-indigo-faint));
  }

  &--completed {
    opacity: 0.8;
  }
}

.v-expansion-panel--active:not(:first-child),
.v-expansion-panel--active + .v-expansion-panel {
  margin-top: 0 !important;
}

.step-item-chevron {
  color: rgb(var(--v-theme-indigo-medium));
}

.v-expansion-panel {
  border-radius: 0 !important;
}

.v-expansion-panel-title {
  padding-right: 16px !important;
  padding-left: 16px !important;
}

:deep(*) {
  .v-expansion-panel-text__wrapper {
    padding: 0 !important;
  }
}

.rotated-90-deg {
  transform: rotate(90deg);
}
.setup-hub-expansion-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  &__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
}
.setup-hub-expansion-panel--footer {
  flex: 0 0 auto;
  height: 52px;
  background-color: rgb(var(--v-theme-white));
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
